import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MessageSquare, Loader2, Users } from 'lucide-react';
import employeeService, { Employee } from '../services/employeeService';

const NiwtDirectory: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const result = await employeeService.listEmployees({ limit: 50 });
        setEmployees(result.employees || []);
      } catch (err) {
        console.error('Failed to load employees:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEmployees();
  }, []);

  const activeEmployees = employees.filter(e => e.niwtStatus === 'active');

  const filteredEmployees = activeEmployees.filter(emp => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      emp.displayName.toLowerCase().includes(q) ||
      (emp.jobTitle || '').toLowerCase().includes(q) ||
      (emp.department || '').toLowerCase().includes(q)
    );
  });

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const handleChatWithNiwt = (email: string) => {
    localStorage.setItem('pending_twin_selection', email);
    navigate('/chat');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-niwt-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading directory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-libre">Niwt Directory</h1>
        <p className="text-gray-600 mt-1">Chat with your co-workers' digital twins</p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, title, or department..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent"
        />
      </div>

      {/* Employee Grid */}
      {filteredEmployees.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">
            {searchQuery ? 'No Niwts match your search' : 'No active Niwts yet'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {searchQuery ? 'Try a different search term' : 'Employees need to be synced and their data ingested first'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEmployees.map(emp => (
            <div
              key={emp.employee_id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-niwt-primary">
                    {getInitials(emp.displayName)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{emp.displayName}</h3>
                  {emp.jobTitle && (
                    <p className="text-sm text-gray-600 truncate">{emp.jobTitle}</p>
                  )}
                  {emp.department && (
                    <p className="text-xs text-gray-500 mt-0.5">{emp.department}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleChatWithNiwt(emp.email)}
                className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-niwt-primary text-white hover:bg-niwt-secondary transition-colors"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Chat with Niwt
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NiwtDirectory;
